import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle, Zap, MessageSquare, Phone, BarChart3,
  Users, Building2, Target, Star, ChevronRight, Megaphone, Play,
  TrendingUp, Shield, Clock
} from "lucide-react";

const FEATURES = [
  { icon: Megaphone, title: "Smart Ad Campaigns", desc: "Connect Meta & Google Ads to auto-capture and route leads directly into your AI pipeline.", color: "bg-blue-50 text-blue-600", delay: 0 },
  { icon: MessageSquare, title: "WhatsApp Automation", desc: "Instant, personalized WhatsApp responses 24/7. Engage leads the moment they show interest.", color: "bg-green-50 text-green-600", delay: 0.1 },
  { icon: Phone, title: "AI Calling Agent", desc: "Qualify leads automatically with human-like AI voice calls — available round the clock.", color: "bg-purple-50 text-purple-600", delay: 0.2 },
  { icon: Target, title: "Lead Scoring", desc: "AI scores every lead 0–100 based on behavior, budget match, and conversation signals.", color: "bg-orange-50 text-orange-600", delay: 0.3 },
  { icon: BarChart3, title: "Real-time Analytics", desc: "Track every metric from ad spend to booking. Identify top-performing campaigns in seconds.", color: "bg-rose-50 text-rose-600", delay: 0.4 },
  { icon: Users, title: "Team Collaboration", desc: "Assign leads, set reminders, and track team performance from a single unified workspace.", color: "bg-indigo-50 text-indigo-600", delay: 0.5 },
];

const JOURNEY_STEPS = [
  { icon: Megaphone, label: "Meta / Google Ads", desc: "Lead captured", bg: "bg-blue-500" },
  { icon: MessageSquare, label: "WhatsApp Connect", desc: "Instant outreach", bg: "bg-green-500" },
  { icon: Phone, label: "AI Calling Agent", desc: "Qualification call", bg: "bg-purple-500" },
  { icon: CheckCircle, label: "Qualified Lead", desc: "Score 80+", bg: "bg-orange-500" },
  { icon: Building2, label: "Site Visit", desc: "Property tour", bg: "bg-rose-500" },
  { icon: Star, label: "Booking", desc: "Deal closed!", bg: "bg-indigo-500" },
];

const TESTIMONIALS = [
  { quote: "PropVision transformed how we handle leads. Our conversion rate went from 12% to 34% in just 3 months. The AI calling agent is a game-changer.", name: "Rajesh Mehta", role: "Founder & Chairman", company: "Elite Estate", avatar: "RM" },
  { quote: "The WhatsApp automation means we never miss a lead, even at 2 AM. Our team now focuses on serious buyers — PropVision handles all the rest.", name: "Priya Kapoor", role: "Sales Head", company: "Elite Estate", avatar: "PK" },
  { quote: "Our Meta campaign CPL dropped from ₹2,200 to ₹450 after connecting to PropVision. The AI optimizes targeting automatically. Simply brilliant.", name: "Arjun Verma", role: "Marketing Manager", company: "Elite Estate", avatar: "AV" },
];

const PRICING = [
  {
    name: "Starter", price: "₹4,999", period: "/month",
    desc: "For individual consultants",
    features: ["1 User", "500 Leads/month", "1 Property", "WhatsApp Bot", "Basic Analytics", "Email Support"],
    cta: "Start Free Trial", popular: false
  },
  {
    name: "Growth", price: "₹12,999", period: "/month",
    desc: "For growing real estate teams",
    features: ["5 Users", "2,000 Leads/month", "5 Properties", "AI Calling Agent", "Advanced Analytics", "Priority Support", "Meta & Google Integration", "Custom WhatsApp Flows"],
    cta: "Start Free Trial", popular: true
  },
  {
    name: "Enterprise", price: "₹29,999", period: "/month",
    desc: "For agencies & developers",
    features: ["Unlimited Users", "Unlimited Leads", "Unlimited Properties", "Custom AI Training", "White-label Option", "Dedicated Account Manager", "API Access", "Custom Integrations"],
    cta: "Contact Sales", popular: false
  },
];

const STATS = [
  { value: "1,200+", label: "Real Estate Teams" },
  { value: "₹8.4 Cr", label: "Revenue Generated" },
  { value: "42%", label: "Avg. Conversion Lift" },
  { value: "3.2M+", label: "Leads Processed" },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-bold text-slate-900 text-lg">PropVision</span>
        </Link>

        <div className="hidden md:flex items-center gap-6 ml-4">
          {["Features", "Journey", "Pricing", "Testimonials"].map(item => (
            <a key={item} href={`#${item.toLowerCase()}`} className="text-sm text-slate-600 hover:text-slate-900 transition-colors font-medium">{item}</a>
          ))}
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <Link to="/dashboard" className="text-sm text-slate-600 hover:text-slate-900 font-medium hidden sm:block" data-testid="signin-link">
            Sign In
          </Link>
          <Link
            to="/dashboard"
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            data-testid="hero-cta-btn"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white font-body" data-testid="landing-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-50/60 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-blue-100">
                <Zap className="w-3.5 h-3.5" />
                <span>AI-Powered Real Estate Growth</span>
              </div>

              <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-900 leading-tight tracking-tight mb-6">
                Turn Every Property<br />
                <span className="text-gradient">Lead Into a Booking</span>
              </h1>

              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                PropVision automates your complete sales journey — from Meta Ads to WhatsApp to AI Calling — so your team focuses on closing deals, not chasing leads.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link
                  to="/dashboard"
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow-lg shadow-blue-600/20"
                  data-testid="hero-main-cta"
                >
                  Start Free Trial <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#journey"
                  className="flex items-center justify-center gap-2 bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 font-semibold px-6 py-3 rounded-xl transition-colors"
                  data-testid="hero-watch-demo"
                >
                  <Play className="w-4 h-4 text-blue-600" /> See How It Works
                </a>
              </div>

              <p className="text-xs text-slate-400">No credit card required · Free 14-day trial · Cancel anytime</p>
            </motion.div>

            {/* Hero Visual */}
            <motion.div
              className="relative hidden lg:block"
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80"
                  alt="Elite Estate Property"
                  className="w-full h-64 object-cover"
                />
                <div className="p-5">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      { label: "New Leads Today", value: "47", change: "+12%", color: "text-blue-600" },
                      { label: "AI Calls Done", value: "28", change: "+8%", color: "text-purple-600" },
                      { label: "Site Visits", value: "9", change: "+3", color: "text-orange-600" },
                    ].map(kpi => (
                      <div key={kpi.label} className="bg-slate-50 rounded-xl p-3">
                        <p className="text-xs text-slate-500 mb-1">{kpi.label}</p>
                        <p className={`text-xl font-heading font-bold ${kpi.color}`}>{kpi.value}</p>
                        <p className="text-xs text-emerald-600 font-semibold">{kpi.change}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <p className="text-xs text-green-700 font-medium">AI Calling Agent — 3 calls active right now</p>
                  </div>
                </div>
              </div>

              {/* Floating stat cards */}
              <motion.div
                className="absolute -left-8 top-16 bg-white rounded-xl shadow-lg border border-slate-200 px-4 py-3 flex items-center gap-3"
                animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">WhatsApp Reply</p>
                  <p className="text-sm font-semibold text-slate-800">Rahul Sharma</p>
                  <p className="text-xs text-green-600">Interested in site visit!</p>
                </div>
              </motion.div>

              <motion.div
                className="absolute -right-4 bottom-24 bg-white rounded-xl shadow-lg border border-slate-200 px-4 py-3"
                animate={{ y: [0, 8, 0] }} transition={{ duration: 3.5, repeat: Infinity }}
              >
                <p className="text-xs text-slate-500 mb-1">AI Lead Score</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-heading font-bold text-blue-600">87</p>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">High Intent</p>
                    <p className="text-xs text-emerald-600">Ready to Book</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                className="text-center"
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
              >
                <p className="text-3xl font-heading font-bold text-white mb-1">{s.value}</p>
                <p className="text-sm text-blue-200">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey */}
      <section id="journey" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Automated Journey</span>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mt-3 mb-4">From Ad Click to Booking</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">PropVision automates every step of the customer journey. Your team only steps in when a lead is truly ready.</p>
          </motion.div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-0 md:gap-0 overflow-x-auto pb-4">
            {JOURNEY_STEPS.map((step, i) => (
              <div key={step.label} className="flex items-center">
                <motion.div
                  className="flex flex-col items-center text-center w-36 shrink-0"
                  initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                >
                  <div className={`w-14 h-14 ${step.bg} rounded-2xl flex items-center justify-center mb-3 shadow-lg`}>
                    <step.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-semibold text-slate-800 text-sm leading-tight">{step.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{step.desc}</p>
                </motion.div>
                {i < JOURNEY_STEPS.length - 1 && (
                  <div className="mx-2 text-slate-300 hidden md:flex items-center">
                    <div className="w-8 h-px bg-slate-200" />
                    <ChevronRight className="w-4 h-4 text-slate-300 -ml-1" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Platform Features</span>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mt-3 mb-4">Everything You Need to Grow</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">One platform that replaces 5 different tools. Campaigns, WhatsApp, AI Calls, CRM, and Analytics — all connected.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:border-blue-100 transition-all duration-300 group"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: f.delay }} viewport={{ once: true }}
              >
                <div className={`w-12 h-12 ${f.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg mb-2">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            >
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Live Dashboard</span>
              <h2 className="font-heading text-4xl font-bold text-slate-900 mt-3 mb-6">Your Entire Business at a Glance</h2>
              <div className="space-y-4">
                {[
                  { icon: TrendingUp, text: "Real-time KPIs: leads, calls, site visits, bookings, revenue" },
                  { icon: Shield, text: "AI-powered lead scoring — know who's ready to buy today" },
                  { icon: Clock, text: "Automated follow-up sequences — no lead slips through the cracks" },
                  { icon: BarChart3, text: "Full funnel analytics from ad spend to booking close rate" },
                ].map(item => (
                  <div key={item.text} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-slate-700 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 mt-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
                data-testid="dashboard-preview-cta"
              >
                Explore Dashboard <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            >
              <div className="bg-slate-900 rounded-2xl p-4 shadow-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <div className="flex-1 bg-slate-700 rounded-md h-5 mx-4" />
                </div>
                <img
                  src="https://images.pexels.com/photos/8134821/pexels-photo-8134821.jpeg?auto=compress&cs=tinysrgb&w=700"
                  alt="PropVision Dashboard"
                  className="rounded-xl w-full object-cover h-72"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Customer Stories</span>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mt-3">Loved by Real Estate Teams</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}, {t.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Pricing</span>
            <h2 className="font-heading text-4xl font-bold text-slate-900 mt-3 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-slate-600">Start free for 14 days. No credit card required.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PRICING.map((plan, i) => (
              <motion.div
                key={plan.name}
                className={`rounded-2xl p-8 border-2 relative ${plan.popular ? "border-blue-500 shadow-xl shadow-blue-500/10" : "border-slate-200"}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <p className="font-heading font-bold text-slate-900 text-xl mb-1">{plan.name}</p>
                <p className="text-xs text-slate-500 mb-4">{plan.desc}</p>
                <div className="flex items-end gap-1 mb-6">
                  <span className="font-heading text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-slate-500 text-sm mb-1">{plan.period}</span>
                </div>
                <ul className="space-y-2.5 mb-8">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-blue-600 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/dashboard"
                  className={`block text-center font-semibold py-3 px-4 rounded-xl transition-colors ${
                    plan.popular
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-slate-100 hover:bg-slate-200 text-slate-800"
                  }`}
                  data-testid={`pricing-${plan.name.toLowerCase()}-cta`}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">Ready to 3x Your Bookings?</h2>
            <p className="text-blue-200 text-lg mb-10">Join 1,200+ real estate teams already using PropVision to automate growth.</p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-bold text-lg px-8 py-4 rounded-2xl transition-colors shadow-xl"
              data-testid="final-cta-btn"
            >
              Start Free Trial <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="text-blue-300 text-sm mt-4">14-day free trial · No credit card · Setup in 5 minutes</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <span className="font-heading font-bold text-white">PropVision</span>
              <span className="text-slate-500 text-xs ml-2">by CodeIntel AI Labs</span>
            </div>
          </div>
          <p className="text-slate-500 text-sm">© 2024 CodeIntel AI Labs. All rights reserved.</p>
          <div className="flex gap-6">
            {["Privacy", "Terms", "Contact"].map(l => (
              <a key={l} href="#" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">{l}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
